<script generics="T" lang="ts">
	import { isAlignRight } from './helpers'
	import type { Column, Columns, Rows, RowsStore, SortStore } from './types'
	import { ColumnType, SortType } from './types'
	import { compareDates, compareNumber, compareStrings } from './utils'
	import { _ } from '@repo/i18n'
	import { Icon } from '@repo/ui'
	import { Tooltip } from '@repo/ui'
	import { Checkbox } from '@repo/ui'
	import {
		ArrowLongDown,
		ArrowLongUp,
		ChevronUpDown,
		QuestionMarkCircle,
	} from '@steeze-ui/heroicons'
	import { DateTime } from 'luxon'
	import type { Snippet } from 'svelte'
	import { type Readable, get, writable } from 'svelte/store'

	interface Props {
		rowClass?: string
		rows?: RowsStore<any>
		columns?: Readable<Columns<T>>
		sort: SortStore
		serverPaginationActive?: boolean
		multiSelect?: boolean
		selectedItems?: any[]
		children?: Snippet
		cell?: Snippet<[any]>
	}

	let {
		rowClass = '',
		rows = writable([]),
		columns = writable([]),
		sort,
		serverPaginationActive = false,
		multiSelect = false,
		selectedItems = $bindable([]),
		children,
		cell,
	}: Props = $props()

	function sortRows(items: Rows<any>, column: Column<any>, direction: SortType) {
		const sorted = items.sort((a: any, b: any) => {
			const [valueA, valueB] = [column.value(a).toString(), column.value(b).toString()]

			if (column.type === ColumnType.date) {
				return compareDates(DateTime.fromISO(valueA), DateTime.fromISO(valueB))
			} else if ([ColumnType.currency, ColumnType.number].includes(column.type)) {
				return compareNumber(parseFloat(valueA), parseFloat(valueB))
			} else {
				return compareStrings(valueA, valueB)
			}
		})

		return direction === SortType.Asc ? sorted : sorted.reverse()
	}

	function reorder(column: Column<any>) {
		if ($sort && $sort.columnId === column.id) {
			$sort.direction = $sort.direction === SortType.Asc ? SortType.Desc : SortType.Asc
		} else {
			$sort = { columnId: column.id, direction: SortType.Asc }
		}

		rows.update((items) => sortRows(items, column, $sort.direction))
	}

	function handleSelectChanged(event: CustomEvent<boolean>) {
		const selected = event.detail
		if (selected) {
			selectedItems = get(rows)
		} else {
			selectedItems = []
		}
	}
</script>

<thead class="sticky top-0">
	{#if children}{@render children()}{:else}
		<tr class={rowClass}>
			{#if multiSelect && $rows.length > 0}
				<th
					class="
            bg-primary-50 dark:bg-primary-900
            sticky top-0 flex h-12 items-center border-r border-b border-gray-200 px-4
            py-1 first:rounded-tl-2xl first:pl-9 last:rounded-tr-2xl last:pr-9
            dark:border-gray-500
          "
				>
					<Checkbox
						checked={selectedItems.length === $rows.length}
						small
						on:change={(event) => handleSelectChanged(event)}
					/>
				</th>
			{/if}

			{#each $columns as column, index (index)}
				{#if cell}{@render cell({ column, index })}{:else}
					<th
						class="sticky top-0 {(column && column.class) ||
							'bg-primary-50 dark:bg-primary-900 h-12 border-r border-b border-gray-200 px-4 py-1 first:rounded-tl-xl first:pl-9 last:rounded-tr-xl last:border-r-0 last:pr-9 dark:border-gray-500'}"
						onclick={() => !serverPaginationActive && !column.disableSort && reorder(column)}
					>
						<div
							class="text-primary-900 flex items-center gap-0.5 truncate whitespace-nowrap dark:text-white {isAlignRight(
								column.type,
							)
								? 'justify-end'
								: ''}"
						>
							{#if column.headerTooltip}
								<Tooltip>
									{#snippet placeholder()}
										<span class="flex items-center gap-1">
											{$_(column.header)}
											<Icon small src={QuestionMarkCircle} />
										</span>
									{/snippet}
									{$_(column.headerTooltip.text)}
								</Tooltip>
							{:else}
								{$_(column.header)}
							{/if}

							{#if !serverPaginationActive && !column.disableSort && column.type !== ColumnType.action}
								<div class="flex items-center">
									{#if $sort && column.id === $sort.columnId}
										<Icon
											small
											src={$sort.direction === SortType.Asc ? ArrowLongUp : ArrowLongDown}
										/>
									{:else}
										<Icon small src={ChevronUpDown} />
									{/if}
								</div>
							{/if}
						</div>
					</th>
				{/if}
			{/each}
		</tr>
	{/if}
</thead>
